import { useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { AppIcon } from './ui/icon'
import { useExpenses } from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useFormatters } from '../lib/format'
import { useLocale } from '../contexts/LocaleContext'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'
import { ExpensesViewTabs } from './expenses/ExpensesViewTabs'
import { useCan } from '../contexts/PermissionsContext'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog'
import {
  recoverableKey, useAcceptRecoverableProposals,
} from '../hooks/useRecoverableCharges'
import { RecoverableChip, type ListedExpense } from './expenses/RecoverableChip'

export function ExpensesPage({ assetUid }: { assetUid?: string } = {}) {
  const { org } = useParams({ strict: false })
  const navigate = useNavigate()
  const { t } = useTranslation('expenses')
  const { locale } = useLocale()
  const { formatMoney } = useFormatters()

  const [page, setPage] = useState(1)
  const pageSize = 20

  const { data: expenses = [], isLoading, error } = useExpenses(org || 'demo', {
    assetUid,
    page,
    limit: pageSize,
    includeRecoverableProposal: true,
  })
  const canWriteFinance = useCan('finance.write')
  const accept = useAcceptRecoverableProposals(org || 'demo')
  const [confirming, setConfirming] = useState(false)
  const [acceptResult, setAcceptResult] = useState<{ accepted: number; skipped: { uid: string; reason: string }[] } | null>(null)

  // The unqualified lines of this page that carry a proposal.
  const proposable = useMemo(() => (expenses as ListedExpense[]).filter(
    (e) => e.recoverable == null && e.recoverable_proposal?.status === 'proposed' && e.recoverable_proposal.rule,
  ), [expenses])
  const countsByValue = useMemo(() => {
    const out: Record<string, number> = {}
    for (const e of proposable) {
      const key = recoverableKey(e.recoverable_proposal!.recoverable, e.recoverable_proposal!.recoverable_ratio)
      out[key] = (out[key] ?? 0) + 1
    }
    return out
  }, [proposable])

  const acceptAll = async () => {
    setConfirming(false)
    const expected: Record<string, string> = {}
    for (const e of proposable) expected[e.uid!] = e.recoverable_proposal!.rule!.uid
    const res = await accept.mutateAsync(proposable.map((e) => e.uid!), expected)
    setAcceptResult({ accepted: res.accepted.length, skipped: res.skipped })
  }

  const breadcrumbs = useMemo(() => [
    { title: t('common:nav.home', 'Home'), path: `/${org}` },
    { title: t('list.title') },
  ], [org, t])

  useBreadcrumbs(breadcrumbs)

  // Amounts are stored in integer cents — divide by 100 for display.
  const formatAmount = (cents: number | null | undefined, currency?: string | null) => {
    if (cents == null) return t('list.no_value')
    return formatMoney(cents, currency)
  }

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return t('list.no_date')
    return new Date(dateString).toLocaleDateString(locale)
  }

  const handleRowClick = (expenseUid: string) => {
    navigate({
      to: '/$org/expenses/$expenseUid',
      params: { org: org || 'demo', expenseUid },
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={SECTION_ICONS.expenses}
        title={t('list.title')}
        description={t('list.subtitle')}
      />

      <ExpensesViewTabs active="list" />

      {assetUid && (
        <div className="flex items-center gap-2 text-sm" data-testid="expenses-asset-filter">
          <Badge variant="info" className="font-normal">{t('list.filter_asset')}</Badge>
          <Link to="/$org/expenses" params={{ org: org || 'demo' }} search={{}} className="text-muted-foreground underline" data-testid="expenses-asset-filter-clear">
            {t('list.filter_clear')}
          </Link>
        </div>
      )}

      {canWriteFinance && proposable.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setConfirming(true)} disabled={accept.isPending} data-testid="expenses-accept-proposals">
            {t('recoverable.accept_page', { count: proposable.length })}
          </Button>
        </div>
      )}
      {acceptResult && (
        <p className="rounded-md border bg-muted/30 px-4 py-2 text-sm" role="status" data-testid="expenses-accept-proposals-result">
          {t('recoverable.accept_result', { accepted: acceptResult.accepted, skipped: acceptResult.skipped.length })}
          {acceptResult.skipped.length > 0 && (
            <span className="text-muted-foreground">
              {' '}({[...new Set(acceptResult.skipped.map((s) => t(`recoverable.skip_reason_${s.reason}`)))].join(', ')})
            </span>
          )}
        </p>
      )}
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent data-testid="expenses-accept-proposals-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('recoverable.accept_confirm_title', { count: proposable.length })}</AlertDialogTitle>
            <AlertDialogDescription>{t('recoverable.accept_confirm_body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="space-y-0.5 text-sm" data-testid="expenses-accept-proposals-counts">
            {(['yes', 'partial', 'no'] as const).filter((k) => countsByValue[k]).map((k) => (
              <li key={k}>{t(`recoverable.accept_confirm_count_${k}`, { count: countsByValue[k] })}</li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="expenses-accept-proposals-cancel">{t('recoverable.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={acceptAll} data-testid="expenses-accept-proposals-confirm">
              {t('recoverable.accept_confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Expenses List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64" data-testid="expenses-loading">
              <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64" data-testid="expenses-error">
              <p className="text-destructive mb-2">{t('list.error')}</p>
              <p className="text-sm text-muted-foreground">
                {(error as any)?.title || (error as any)?.message || 'Unknown error occurred'}
              </p>
            </div>
          ) : expenses.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider" data-testid="header-label">
                      {t('list.column_label')}
                    </th>
                    <th className="px-6 py-3 text-right text-[11px] font-medium text-muted-foreground uppercase tracking-wider" data-testid="header-amount">
                      {t('list.column_amount')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider" data-testid="header-recoverable">
                      {t('recoverable.column')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider" data-testid="header-date">
                      {t('list.column_date')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider" data-testid="header-asset">
                      {t('list.column_asset')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider" data-testid="header-supplier">
                      {t('list.column_supplier')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {expenses.map((expense) => {
                    const asset = (expense as any).asset as { uid?: string; name?: string } | null | undefined
                    const supplier = (expense as any).supplier as { uid?: string; name?: string } | null | undefined
                    return (
                      <tr
                        key={expense.uid}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(expense.uid!)}
                        data-testid={`row-expense-${expense.uid}`}
                      >
                        <td className="px-6 py-4">
                          <Link
                            to="/$org/expenses/$expenseUid"
                            params={{ org: org || 'demo', expenseUid: expense.uid! }}
                            className="text-sm font-medium text-foreground hover:text-primary hover:underline transition-colors"
                            data-testid={`expense-label-${expense.uid}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {expense.label || '—'}
                          </Link>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm font-medium text-foreground" data-testid={`expense-amount-${expense.uid}`}>
                            {formatAmount(expense.amount, expense.currency)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <RecoverableChip expense={expense as ListedExpense} />
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-muted-foreground">
                            {formatDate(expense.accrual_date || expense.expense_date)}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-testid={`expense-asset-${expense.uid}`}>
                          {expense.asset_uid ? (
                            <Link
                              to="/$org/assets/$assetUid"
                              params={{ org: org || 'demo', assetUid: expense.asset_uid }}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`expense-asset-link-${expense.uid}`}
                            >
                              <Badge variant="outline" className="gap-1 text-xs font-normal max-w-[16rem] truncate hover:bg-muted">
                                <AppIcon path={SECTION_ICONS.assets} size="xs" className="shrink-0" />
                                {asset?.name || t('list.asset_unknown')}
                              </Badge>
                            </Link>
                          ) : (
                            <span className="text-sm text-muted-foreground/60">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-muted-foreground">
                            {supplier?.name || t('list.no_value')}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              live
              className="py-16"
              icon={SECTION_ICONS.expenses}
              title={t('list.empty')}
              data-testid="expenses-empty"
            />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {expenses.length >= pageSize && (
        <div className="flex justify-center space-x-2">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            data-testid="expenses-prev-page"
          >
            {t('list.page_previous')}
          </Button>
          <span className="px-4 py-2 text-sm text-muted-foreground">
            {t('list.page_label', { page })}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage((p) => p + 1)}
            disabled={expenses.length < pageSize}
            data-testid="expenses-next-page"
          >
            {t('list.page_next')}
          </Button>
        </div>
      )}
    </div>
  )
}
